// v0.80.11 — diagnostics for Lab. On a phone there is no console, and the
// runbook's first rule for a failed userOp is "print the full .cause chain":
// the real error nests 2–3 levels down and the top-level message is generic.
// So failures record themselves here and Lab shows the last few, flattened.

export interface DiagEntry {
  at: string; // ISO
  scope: string; // "send" | "drop/create" | "drop/release" | …
  lines: string[]; // message, then each .cause's message, deepest last
}

const MAX = 3;
const entries: DiagEntry[] = [];

function flatten(e: unknown): string[] {
  const out: string[] = [];
  let cur: unknown = e;
  let depth = 0;
  while (cur && depth < 8) {
    if (cur instanceof Error) {
      // viem errors put the useful part in `shortMessage`/`details`.
      const v = cur as Error & { shortMessage?: string; details?: string; cause?: unknown };
      const bits = [v.name, v.shortMessage ?? v.message, v.details].filter(Boolean);
      out.push(bits.join(" · ").slice(0, 400));
      cur = v.cause;
    } else if (typeof cur === "object" && cur !== null) {
      out.push(JSON.stringify(cur).slice(0, 400));
      cur = (cur as { cause?: unknown }).cause;
    } else {
      out.push(String(cur).slice(0, 400));
      cur = null;
    }
    depth++;
  }
  return out;
}

// v0.90.22 — a cancelled Face ID is not a failure. WebAuthn reports a cancel,
// a timeout and "no passkey" as one `NotAllowedError` (decisions-log), and
// viem/ox wrap it in `CredentialRequestFailedError`; inside a payment the
// passkey has just logged the person in, so a cancel or timeout is what it
// means. The flows show one line and re-arm the slide instead of a failure
// card that blames the balance (seen 23 Sep 2026: a cancelled Face ID read
// "Drop didn't go through · Check the balance covers the amount").
export function isPasskeyCancel(e: unknown): boolean {
  let cur: unknown = e;
  let depth = 0;
  while (cur && depth < 8) {
    if (typeof cur === "object" && cur !== null) {
      const n = (cur as { name?: unknown }).name;
      if (n === "NotAllowedError" || n === "CredentialRequestFailedError") return true;
      const m = (cur as { message?: unknown }).message;
      if (typeof m === "string" && /NotAllowedError|not allowed by the user agent/i.test(m)) return true;
      cur = (cur as { cause?: unknown }).cause;
    } else break;
    depth++;
  }
  return false;
}
export const PASSKEY_CANCEL_LINE = "Face ID didn't complete. Tap again when you're ready.";

/** Every message in an error's cause chain, as one string (for matching, never shown). */
function chainText(e: unknown): string {
  const out: string[] = [];
  let cur: unknown = e;
  let depth = 0;
  while (cur && depth < 8) {
    if (typeof cur === "object" && cur !== null) {
      for (const k of ["message", "shortMessage", "details"] as const) {
        const v = (cur as Record<string, unknown>)[k];
        if (typeof v === "string") out.push(v);
      }
      cur = (cur as { cause?: unknown }).cause;
    } else { out.push(String(cur)); break; }
    depth++;
  }
  return out.join(" | ");
}

/** 1.4.77 (v1.9's note, 22 Oct) — the failure screen names a cause only when the refusal says it, and never guesses one.
 *  Circle's Gas Station cap ("Exceeded max daily native token of the policy") was shown as "Check the balance covers the
 *  amount" all evening of 21 Oct. */
export const FAIL_GAS = "M can't cover network fees right now. Nothing moved — try again later.";
export const FAIL_FUNDS = "Not enough in your wallet for this. Nothing moved.";
export const FAIL_OTHER = "Nothing moved. Try again in a moment.";
/** 1.4.77 — a failure screen's title on one line (the swap's rule, 1.4.24): the font steps down until it fits. */
export function fitFailTitle(root: HTMLElement): void {
  const h = root.querySelector<HTMLElement>(".pay-fail");
  if (!h) return;
  const avail = (root.clientWidth || window.innerWidth) - 32;
  const start = parseFloat(getComputedStyle(h).fontSize) || 28;
  for (let fs = start; fs > 14 && h.scrollWidth > avail; fs--) h.style.fontSize = `${fs - 1}px`;
}
export function failureLine(e: unknown): string {
  const t = chainText(e);
  if (/max daily native token|exceeded .*policy|paymaster.*(denied|rejected|limit)|sponsorship.*(denied|rejected|limit)/i.test(t)) return FAIL_GAS;
  if (/transfer amount exceeds balance|ERC20InsufficientBalance|insufficient (funds|balance)|exceeds balance/i.test(t)) return FAIL_FUNDS;
  return FAIL_OTHER;
}

export function recordError(scope: string, e: unknown): void {
  entries.unshift({ at: new Date().toISOString(), scope, lines: flatten(e) });
  if (entries.length > MAX) entries.length = MAX;
}

export function lastErrors(): DiagEntry[] {
  return entries;
}

// v1.1.17 — measurements are not errors: the M® Note KDF timing (brief §8.6)
// used to go through recordError and showed under "Last errors" in Lab.
const measures: DiagEntry[] = [];
export function recordMeasure(scope: string, text: string): void {
  measures.unshift({ at: new Date().toISOString(), scope, lines: [text] });
  if (measures.length > MAX) measures.length = MAX;
}
export function lastMeasures(): DiagEntry[] {
  return measures;
}
